import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "../lib/api";
import { useApiErrorHandler } from "../lib/session";
import type { PlaceSummary } from "../lib/types";
import { Notice, Spinner, placeTypeLabel, statusLabel } from "../components/bits";

export default function Places() {
  const handleError = useApiErrorHandler();
  const [places, setPlaces] = useState<PlaceSummary[] | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let active = true;
    api
      .places()
      .then((result) => active && setPlaces(result.places))
      .catch((err) => active && setError(handleError(err)));
    return () => {
      active = false;
    };
  }, [handleError]);

  return (
    <div className="screen screen--tabbed">
      <h1 className="screen-title">Orte</h1>
      <p className="screen-sub">Überall, wo du gespielt hast, und wie es gelaufen ist.</p>

      {error ? <Notice tone="error">{error}</Notice> : null}
      {!places && !error ? <Spinner /> : null}

      {places && places.length === 0 ? (
        <div className="empty">
          <h2>Noch keine Orte</h2>
          <p>Dein erster Besuch erscheint hier — und bleibt hier.</p>
        </div>
      ) : null}

      <div className="stack">
        {places?.map((place) => {
          const pct = place.target_points > 0
            ? Math.min(100, (place.earned_points / place.target_points) * 100)
            : 0;
          return (
            <Link key={place.id} to={`/places/${place.id}`} className="place-row">
              <div className="row spread" style={{ alignItems: "baseline" }}>
                <h2 className="place-row__name">{place.name}</h2>
                {place.conquered ? (
                  <span className="pill pill--won">Erobert</span>
                ) : (
                  <span className="row" style={{ gap: 6 }}>
                    {place.visited ? (
                      <span className="pill pill--visited">{statusLabel("visited")}</span>
                    ) : null}
                    <span className="pill">
                      {place.earned_points}/{place.target_points}
                    </span>
                  </span>
                )}
              </div>
              <div className="bar" style={{ height: 7, marginTop: 10 }}>
                <div
                  className="bar__fill"
                  style={{
                    width: `${pct}%`,
                    background: place.conquered ? "var(--accent)" : undefined,
                  }}
                />
              </div>
              <p className="tiny muted" style={{ margin: "9px 0 0" }}>
                {placeTypeLabel(place.place_type)}
                {place.country_code ? ` · ${place.country_code}` : ""} ·{" "}
                {place.visit_count} {place.visit_count === 1 ? "Besuch" : "Besuche"}
                {place.conquered_at
                  ? ` · erobert am ${new Date(place.conquered_at).toLocaleDateString("de-DE")}`
                  : ""}
              </p>
            </Link>
          );
        })}
      </div>
    </div>
  );
}
